import { getBlockFile } from 'client/reducers/blocksReducer'
import { Files } from 'client/store/files/filesReducer'
import { EntityUnionType } from 'common/types/entities/EntityInterface'
import { TFunction } from 'i18next'
import React, { useMemo, useState } from 'react'
import { useDrag } from 'react-dnd'
import { useSelector } from 'react-redux'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'
import { BlockType } from '../types'
import { BlockShareDialog } from './block-share-dialog'

const demoImage =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAV4AAACWBAMAAABkyf1EAAAAG1BMVEXh5eZte4t7iJaKlaGnsLjS19rEys+Yoq21vcMcLj6CAAAACXBIWXMAAA7EAAAOxAGVKw4bAAADBUlEQVR4nO2YTW/aQBCGJwGTHj0LITnaadJeMf3KMSZSe8WpqvaI00Ov0CrtFQ7N7+7MGuMFKuEDbBzpfSTwxrMjP2z2Y4AIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGgcR8xs3o5r9Az44DI1UF/mVzV6NsTXXFzEzJPdPRvieypvP/hqd88G+VLa292zSb4nNVya5BvwfGfPJvlSMpW3W76U9yxsxy/HneS1bnLByPxe9lTftVhnaK410Em68yxc5XvxXQyI7mRjk3WXncp+cZXaNuVyb+74urGZxCSN5M+e+i7zvfjmIQUxX3BXnLrdx5nhj7oGA+Yh9x1fJ9bm7i+W6wvmhNMq34uvjM8JP9BX2Ygznkq7TyeGqCWDmxvH14kdd8d0J37HZkL3XOV78ZVLdibXeEqZuHTEqyN++bm255WvEzvq69jKVDrVe+Eq35evPpcW8txzdZvIa0xpJPeSQeXrxD4PyHrPNByHq3xfvjN1y2Sc+2R9SF7JjTpEla8TU9pyL5lLQ+Zvme/FV+ZvYiufc/tIu9eKU6wueVj5OjGib58SaccTsuNa5nvxzSOKS9+wcrLLZzlmxf7r+I60PxV95DPFPn3TwfJ5vdq+LTYfHrd8axQi+/CViZpExR3XaWs+OLFFb2zbZZ8y34evrvkk3PbdWm8bMW0XfcJVvg/fluyrqbbur9acUlV197ONuaL7b9EnXOX78E1lmdj9Xv6prtPWeeHOFfE91jOl75wXB58U1vc7R3r23sh5erOxpjbOY3c+vKNA97OWnMe5+Jb5B/Ytvr+NbW3zhs2601a946438yc542sZWzNkOd/K/AP7Vt+P82U96e6xm/Xk2tgzT2OZBAtmo+dw7qWe1N8frguhkW2s+QY/zQP935duzXu6vyz66J63zH8WLKKnNqjNFy0l0uiJLeqT2nry0HXv/sht3T5/ao3aHJm/nZTr/GLYDNo+yrJ9ooX685m+MnmHq99UAAAAAAAAAAAAAAAAAAAAAAAAAABA4/kHG6uDwenboGUAAAAASUVORK5CYII'

interface BlockProps {
  block: BlockType
  remove: () => void
  isRemoveAllowed?: boolean
  isMobile?: boolean
  isSharingAllowed?: boolean
  isDisabled?: boolean
  isBlockInUse?: boolean
  addToTheBottom: ({
    blockEntities,
    t,
  }: {
    blockEntities: EntityUnionType[]
    t: TFunction
  }) => void
}

interface BlocksState {
  userBlocks: BlockType[]
  masterBlocks: BlockType[]
  commonBlocks: BlockType[]
  userFiles: Files
  masterFiles: Files
  commonFiles: Files
}

export const Block = ({
  block,
  remove,
  isRemoveAllowed,
  isMobile,
  isSharingAllowed = false,
  isDisabled = false,
  isBlockInUse = false,
  addToTheBottom,
}: BlockProps) => {
  const { t } = useTypedTranslation()
  const [isShareDialogOpen, setShareDialogOpen] = useState(false)
  const desktopFile = useSelector((state: { blocks: BlocksState }) =>
    getBlockFile(state.blocks, block.desktop_preview?.id),
  )
  const mobileFile = useSelector((state: { blocks: BlocksState }) =>
    getBlockFile(state.blocks, block.mobile_preview?.id),
  )
  const [, drag] = useDrag({
    item: {
      id: block.id,
      entities: block.content,
      type: 'BLOCK',
      isMaster: Boolean(block.isMaster),
    },
    canDrag: !isDisabled,
  })

  const safeRemove = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()
    if (
      window.confirm(
        t(
          block.isMaster
            ? 'components.core.blocks.confirm_remove_master'
            : 'components.core.blocks.confirm_remove',
        ),
      )
    ) {
      remove()
    }
  }

  const title = useMemo(() => {
    if (isDisabled && isBlockInUse) {
      return t('components.core.blocks.master_block.disabled_message')
    } else if (isDisabled) {
      return t('components.core.blocks.common_block.disabled_message')
    }

    return
  }, [t, isDisabled, isBlockInUse])

  return (
    <>
      <button
        ref={drag}
        type="button"
        aria-disabled={isDisabled}
        onClick={() =>
          !isDisabled &&
          addToTheBottom({ blockEntities: block.content, t: t as TFunction })
        }
        title={title}
        className={twMerge(
          'relative mt-10 mr-0.5 ml-0.5 w-full cursor-pointer border-gray-100 border-b pb-5 opacity-100 last:border-0 [&:first-of-type]:mt-2.5',
          isDisabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <div
          className={twMerge(
            'flex h-auto min-h-[120px] w-full items-center justify-center [&>img]:max-h-full [&>img]:max-w-full',
            isMobile && '[&>img]:max-h-[300px] [&>img]:max-w-[150px]',
          )}
        >
          <img
            alt={title}
            src={
              isMobile && mobileFile
                ? mobileFile.path
                : desktopFile
                  ? desktopFile.path
                  : demoImage
            }
          />
        </div>
        <div className="flex w-full items-center justify-between p-2">
          <span className="mr-auto text-M">{block.title}</span>
          <div className="flex gap-3.5">
            {isSharingAllowed && (
              <button
                type="button"
                className="cursor-pointer px-1"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  setShareDialogOpen(true)
                }}
              >
                <i className="fa-regular fa-share-nodes text-S text-iconSecondary" />
              </button>
            )}
            {isRemoveAllowed && (
              <button
                type="button"
                className="cursor-pointer px-1"
                onClick={safeRemove}
              >
                <i className="fa-regular fa-trash text-S text-iconSecondary" />
              </button>
            )}
          </div>
        </div>
      </button>
      {isShareDialogOpen && (
        <BlockShareDialog
          block={block}
          close={() => setShareDialogOpen(false)}
        />
      )}
    </>
  )
}
